import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getHaryanaWeather } from "../../../Rest_api/RestApi";
import { HarayanaWeatherData } from "../../../models/rajasthan";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { GnaRuvnlFooter } from "../components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import getMonths, { DataTabs, DEFAULT_DATES } from "../Demand/Data";
import Loading from "../../../components/Loading";
import FilterMenu from "../../../components/FilterMenu";
import { FilterTab } from "../../../components/Filters";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../const";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { generateDateRanges } from "../../Rajasthan/Generation/Data";

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const chartSuffixes = [
  {
    suffix: "temperature_c",
    displayName: "Temperature",
    stackSuffix: "temperature_c",
  },
  {
    suffix: "humidity_percent",
    displayName: "Humidity",
    stackSuffix: "humidity_percent",
  },
];

const Weather = () => {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[2]);
  const [dataTab, setDataTab] = useState(DataTabs[0]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [data, setData] = useState<HarayanaWeatherData[]>([]);
  const [uniqueDate, setUniqueDate] = useState(generateDateRanges());
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedDate, setSelectedDate] = useState<
    { value: string; label: string }[]
  >([
    { value: "2025-01-01,2025-01-31", label: "JAN-25" },
    { value: "2024-01-01,2024-01-31", label: "JAN-24" },
  ] as any);
  const [dates, setDate] = useState({
    startDate: new Date("2025-01-06"),
    endDate: new Date("2025-01-13"),
    key: "selection",
  });

  useEffect(() => {
    _handlefetchData();
  }, [dates, selectedDate]);

  async function _handlefetchData() {
    setLoading(true);

    try {
      const promises = selectedDate.map(async (dateOption) => {
        const [startDate, endDate] = dateOption.value.split(",");
        const rangeData = await getHaryanaWeather(startDate, endDate);
        return rangeData.map((item) => ({
          ...item,
          dates: dateOption.label ?? item.dates,
        }));
      });
      const results = await Promise.all(promises);

      const newData = results.flat();

      setData(newData);
      setLocation(
        Array.from(new Set(newData.map((item) => item.location))).sort()
      );
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.slot))));
    } catch (error) {
      console.error("Error fetching weather data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }
    if (selectedLocation.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedLocation.includes(item.location)
      );
    }
    const par: ChartDataParams = {
      keys: ["block", "dateIndex"],
      groupBy: ["dates", "location"],
      volumeKey: ``,
      aggregationKeys: ["temperature_c", "humidity_percent"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    dates,
    selectedBlock,
    selectedDate,
    selectedInterval.values,
    selectedLocation,
  ]);

  const xDataKey = selectedBlock.length < 1 ? "block" : "dateIndex";

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    // Determine the appropriate unit suffix based on the parameter type
    const unitSuffix =
      suffix === "temperature_c"
        ? " (°C)"
        : suffix === "humidity_percent"
        ? " (%)"
        : "";

    // Parse the combined key to extract location and date range
    const parts = key.split("_");
    const location = parts[0];
    // If there are multiple parts, the rest is the date range
    const dateInfo = parts.length > 1 ? ` - ${parts.slice(1).join(" ")}` : "";

    return {
      dataKey: `${key}_${suffix}`,
      name: selectedInterval.isTime
        ? `${location}${dateInfo} ${displayName}${unitSuffix}`
        : `${location}${dateInfo} ${displayName}${unitSuffix}`,
      legendColor: undefined,
      // Ensure consistent colors for the same location across different date ranges
      stroke: stringToConsistentColor(`${location}_${suffix}`),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors
          ) as any
      )
    );
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    itemsPerPage: 96,
    unit: "",
    extraKeys: selectedInterval.isTime
      ? [
          selectedBlock.length < 1 ? "block" : "dateIndex",
          selectedInterval.values[1],
        ]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values.map((value) =>
      value.replace("dateIndex", "Day")
    ),
  };

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Weather Parameters"
            items={chartSuffixes.map((item) => item.displayName)}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            onSelect={(item, index) => {
              if (selectedSuffix.length > 1) {
                setSelectedSuffix([chartSuffixes[index]]);
              } else {
                if (selectedSuffix.includes(chartSuffixes[index])) {
                  return;
                }
                setSelectedSuffix([...selectedSuffix, chartSuffixes[index]]);
              }

              setSelectedSuffix((prev) =>
                prev.sort(
                  (a, b) => chartSuffixes.indexOf(a) - chartSuffixes.indexOf(b)
                )
              );
            }}
          />
          <FilterMenu
            title="Location"
            items={location}
            selectedItems={selectedLocation}
            setSelectedItems={setSelectedLocation}
          />
        </div>
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Weather Forecast</h2>
            <div className="flex gap-x-6  justify-end">
              <FilterTab
                options={DataTabs.map((item) => item.label)}
                selected={[dataTab.label]}
                onClick={(option, index) => {
                  setDataTab(DataTabs[index]);
                }}
              />
              {/* <DynamicDatePicker
                minDate="2019-01-01"
                setDate={(date) => addDate(date[0]!)!}
              /> */}

              <SearchBox
                options={uniqueDate}
                isMany={true}
                placeholder="Date Range"
                selectedOptions={selectedDate}
                onChange={(item: any) => {
                  setSelectedDate(
                    item.map(
                      (opt: any) =>
                        uniqueDate.find((date) => date.value === opt.value) ??
                        opt
                    )
                  );
                }}
              />

              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(tablePar as any);

                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(tablePar as any);

                  dataTab.label === "Table" &&
                    downloadChartTableCSV(tablePar as any);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          <div className="h-full w-full">
            {dataTab.label === "Chart" && (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={selectedBlock.length < 1 && selectedInterval.isTime}
                onlyTitle={false}
                barCategoryGap={10}
                unit={""}
                yAxisLabel={""}
                legends={getLegends()}
                xDataKey={xDataKey}
                xTick={
                  selectedInterval.isTime && selectedBlock.length == 0
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined
                }
                secondXDataKey={
                  selectedInterval.isTime && selectedBlock.length == 0
                    ? selectedInterval.values[1]
                    : undefined
                }
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...(tablePar as any)} />
            )}
            {dataTab.label === "Table" && <ChartTable {...(tablePar as any)} />}
          </div>
          <GnaRuvnlFooter
            source="Source: HPPC Office"
            note="(1) Total Load = Unrestricted Demand = Actual  Drawal + Load Shedding; (2) Load Shedding has been distributed across time blocks based on the actual duration of load shedding"
          />
        </div>
      </div>
    </>
  );
};

export default Weather;
